import React, {useState, useEffect} from 'react';
import { useLocation } from 'react-router-dom';

import { Container, TextField, Button, Typography } from '@mui/material';
import SailingIcon from '@mui/icons-material/Sailing';
import { useStateContext } from '../contexts/ContextProvider';
import { useNavigate } from 'react-router-dom';
import { ForgotPassword, NewPassword } from '../components';
import { motion } from 'framer-motion';
import logo from '../data/logo192.png';
import percentSVG from '../data/percent.svg'; // Adjust the path as needed


import { useMutation, gql } from '@apollo/client';

const LOGIN_MUTATION = gql`
  mutation Login($identifier: String!, $password: String!) {
    login(input: { identifier: $identifier, password: $password }) {
      jwt
      user {
        id
        username
        email
      }
    }
  }
`;


const Login = () => {

  localStorage.removeItem('jwt');
  const location = useLocation();


  const { setIsLogged } = useStateContext();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState(""); // Add this state
  const [isForgotPassword, setIsForgotPassword] = useState(false); // State for toggling between login and password reset
  const [newPassword, setNewPassword] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);


  const getQueryParams = () => {
      const searchParams = new URLSearchParams(location.search);
      return {
          resetPassword: searchParams.get('resetPassword') === 'true',
          serverCode: searchParams.get('code')
      };
  };
  
  const { resetPassword, serverCode } = getQueryParams();


  const handleLoginSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission behavior
    handleLogin(email, password); // Call handleLogin with email and password
    }

  const [login, { data, loading, error }] = useMutation(LOGIN_MUTATION);
  useEffect(() => {
    if (resetPassword && serverCode) {
        // Save the token and set resetPassword state
        setNewPassword(true);
    }
    }, [resetPassword, serverCode]);


    //animation - framer motion params
    const errorMessageVariants = {
      initial: { x: 0 },
      animate: { 
        x: [0, -10, 10, -10, 10, 0], // Move left to right and back to original
        transition: { duration: 0.4 } // Duration of the animation
      }
    };

    const logoVariants = {
      hidden: { x: -100, opacity: 0 },
      visible: { x: 0, opacity: 1, transition: { type: "spring", stiffness: 120 } },
    };

    const textVariants = {
      hidden: { x: 100, opacity: 0 },
      visible: { x: 0, opacity: 1, transition: { type: "spring", stiffness: 120 } },
    };


  const textFieldStyles = {
      '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
          borderColor: 'var(--black1)', // Focus border color
        },
      },
      '& .MuiInputLabel-outlined.Mui-focused': {
        color: 'var(--black1)', // Focus label color
      },
    };

  const handleLogin = async (emailOrUsername, password) => {
    try {
      const { data } = await login({ variables: { identifier: emailOrUsername, password } });
      
      if (data && data.login.jwt) {
        localStorage.setItem('jwt', data.login.jwt);
        localStorage.setItem('userId', data.login.user.id);  // Storing the user ID
        localStorage.setItem('username', data.login.user.username);  // Storing the username
        localStorage.setItem('user_mail', data.login.user.email); // easier to have mail as well

        setIsLogged(true);
      }
    } catch (err) {
      // Handle any errors that might occur during the login process
      console.error("Error logging in:", err);
      setIsLogged(false);
      setErrorMessage("Check username and password.");  // Store the error message
      setAnimationKey(prevKey => prevKey + 1);

    }
  }

 
  if (isForgotPassword) {
    // Return your ForgotPassword component/content
    return <ForgotPassword onCancel={() => setIsForgotPassword(false)} />;
  }
  if (newPassword) {
    // Return your ForgotPassword component/content
    return <NewPassword serverCode={serverCode} onCancel={() => setNewPassword(false)} />;
  }
  


  return (
    <Container 
      component="main" 
      maxWidth="xs" 
      className="flex flex-col items-center justify-center h-screen "
    >
    <div className='mt-[-40%] sm:mt-[-25%] md:sm:mt-[-20%]'>
    <div className="my-10 flex flex-col items-center ">
      <motion.img
        src={logo}
        alt="Logo"
        className="h-14 w-14 mb-2 mt-4"
        variants={logoVariants}
        initial="hidden"
        animate="visible"
      />
      <motion.span
        className="text-gray-700 bg-gray-100 p-2 px-4  text-xl font-semibold"
        variants={textVariants}
        initial="hidden"
        animate="visible"
      >
        STdiscount
      </motion.span>
    </div>
      <div className="p-8 flex flex-col items-center bg-white shadow-lg rounded-md">
        
        <Typography component="h1" variant="h5" className="mb-4">
          Log In
        </Typography>
        <form className="w-full mt-2" onSubmit={handleLoginSubmit} noValidate>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            className="mb-4"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={textFieldStyles}
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            className="mb-4"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={textFieldStyles}
          />

          {/* Display error message */}
          {errorMessage && (
            <motion.div
              variants={errorMessageVariants}
              initial="initial"
              animate="animate"
              key={animationKey}
            >
              <Typography variant="body2" color="error">
                {errorMessage}
              </Typography>
            </motion.div>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            className="mt-8 mb-4"
            sx = {{
              backgroundColor: 'var(--black1)',
              color: 'var(--yellow1)',
              '&:focus': {
                  backgroundColor: 'var(--black1)', // Lighter black on click (focus)
                }
            }}
          >
            Prijavi se
          </Button>
          <Typography
          variant="body2"
          className="text-gray-600 cursor-pointer hover:text-gray-900 my-2"
          onClick={() => setIsForgotPassword(true)}>
            Forgot password?
          </Typography>
        </form>
      </div>
    </div>
    </Container>
  );
};

export default Login;
